import { colors } from 'styleguide/colors';
import { P2 } from 'styleguide/Texts';
import React, { useRef, useState } from 'react';
import { Spacing } from 'styleguide/spacing';
import styled, { ThemeProps } from 'styled-components';
import useIsOnScreen from 'utils/useIsOnScreen';
import fonts from 'styleguide/fonts';
import { Theme } from 'styleguide/types';

interface TabDescriptor<T = string> {
  id: T;
  title: string;
}

const TabButtonText = styled(P2)<{ isActive: boolean; isHovering: boolean }>`
  cursor: pointer;
  color: ${props => (props.isActive || props.isHovering ? colors.primary : undefined)};
  font-family: ${props => (props.isActive ? fonts.medium.family : fonts.regular.family)};
  font-weight: ${props => (props.isActive ? fonts.medium.weight : fonts.regular.weight)};
  border-bottom: solid;
  border-bottom-width: ${props => (props.isActive ? 2 : 0)};
  border-bottom-color: ${props =>
    props.isActive || props.isHovering ? colors.primary : undefined};
  padding-bottom: ${Spacing.md - 2}px;
  padding-inline-start: ${Spacing.xs}px;
  padding-inline-end: ${Spacing.xs}px;
  flex-wrap: nowrap;
  white-space: nowrap;
`;

const TabButton = ({
  title,
  active,
  onSelected,
}: {
  title: string;
  active: boolean;
  onSelected: () => void;
}) => {
  const [isHovering, setIsHovering] = useState(false);

  return (
    <TabButtonText
      isActive={active}
      onClick={onSelected}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      isHovering={isHovering}
    >
      {title}
    </TabButtonText>
  );
};

const TabButtonsContainer = styled.div`
  flex-direction: row;
  display: flex;
  gap: ${Spacing.sm}px;
  overflow-x: scroll;

  /* Hide the scroll indicator */
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  ::-webkit-scrollbar {
    display: none; /* Chrome */
  }
`;

const Container = styled.div`
  position: relative;
`;

const RightMoreTabsIndicator = styled.div`
  position: absolute;
  width: 50px;
  top: 0px;
  right: 0px;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0) 0%,
    ${(props: ThemeProps<Theme>) => props.theme.secondary} 100%
  );
  height: 100%;
  display: grid;
`;

const LeftMoreTabsIndicator = styled.div`
  position: absolute;
  width: 50px;
  top: 0px;
  left: 0px;
  background: linear-gradient(
    90deg,
    ${(props: ThemeProps<Theme>) =>
        props.theme.name === 'light' ? colors.greyHiperLight : colors.greyHiperDark}
      0%,
    rgba(0, 0, 0, 0) 100%
  );
  height: 100%;
  display: grid;
`;

type TabsProps<T extends string = string> = {
  tabs: readonly TabDescriptor<T>[];
  selectedTabIdentifier: T;
  onTabClicked: (tabId: T) => void;
};

export const Tabs = <T extends string = string>(props: TabsProps<T>) => {
  const { tabs, selectedTabIdentifier: selectedTabId, onTabClicked } = props;
  const rightEdgeOfTabs = useRef<HTMLDivElement>(null);
  const isRightMostTabVisible = useIsOnScreen(rightEdgeOfTabs);
  const leftEdgeOfTabs = useRef<HTMLDivElement>(null);
  const isLeftMostTabVisible = useIsOnScreen(leftEdgeOfTabs);

  return (
    <Container>
      {!isLeftMostTabVisible && <LeftMoreTabsIndicator />}
      {!isRightMostTabVisible && <RightMoreTabsIndicator />}
      <TabButtonsContainer>
        <div ref={leftEdgeOfTabs} />
        {tabs.map((tab, index) => {
          return (
            <TabButton
              key={index}
              active={tab.id === selectedTabId}
              title={tab.title}
              onSelected={() => onTabClicked(tab.id)}
            />
          );
        })}
        <div ref={rightEdgeOfTabs} />
      </TabButtonsContainer>
    </Container>
  );
};
